import { useApp, useBehavior, useInput, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, ActionTypeEnum, TInputData, TWAction, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, getFieldName } from '@libs/utils';
import React, { useMemo, useCallback, useState } from 'react';
import { CoreInputView } from '@core/widgets';
import { TInputOption } from '../../types/options';
import { ConsumerActionPlayerNameEnums } from '../../types/enums';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';

const getInputMode = (options: TInputOption): 'text' | 'numeric' | 'decimal' | 'email' => {
    const inputModeMap: { [key: string]: 'text' | 'numeric' | 'decimal' | 'email' } = {
        number: 'decimal',
        decimal: 'decimal',
        numericPassword: 'numeric',
        email: 'email',
    };

    return inputModeMap[options?.inputType ?? 'text'] || 'text';
};
const getInputType = (options: TInputOption): 'text' | 'number' | 'password' | 'decimal' => {
    const inputTypeMap: { [key: string]: 'text' | 'number' | 'password' | 'decimal' } = {
        number: 'number',
        decimal: 'number',
        password: 'password',
        numericPassword: 'password',
    };

    return inputTypeMap[options?.inputType ?? 'text'] || 'text';
};

type TTextInput = {
    inputRef?: React.RefObject<HTMLInputElement | null>;
    autoComplete?: string;
} & TWidgetProps<TInputData, TInputOption>;

const findPlayerOnClickAction = (actions: TWAction[] | undefined, name: ConsumerActionPlayerNameEnums): TWAction | undefined =>
    actions?.find((a) => a?.type === ActionTypeEnum.Player && a?.actionType === ActionActionTypeEnum.onClick && a?.name === name);

const InputText = ({ widget, inputRef, autoComplete = 'off' }: TTextInput) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const name = getFieldName(options.autofill);
    const [initialIsError] = useState(() => !!options.isError);
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const {
        value,
        isFocused,
        isFilled,
        isError,
        errorMessage,
        label,
        helperText,
        handleChange,
        handleBlur,
        handleFocus,
        failedValidationInternalId,
    } = useInput(widget, { isHidden, isDisabled, triggerActionsByType, setModal });
    const isPassword = options.inputType === 'numericPassword' || options.inputType === 'password';

    const inputAutoComplete = camelCaseToKebabCase(isPassword ? 'off' : (options.autofill ?? autoComplete));

    const helperAction = useMemo(() => findPlayerOnClickAction(widget.actions, ConsumerActionPlayerNameEnums.showHelperDescription), [widget.actions]);
    const errorHelperAction = useMemo(
        () => findPlayerOnClickAction(widget.actions, ConsumerActionPlayerNameEnums.showErrorHelperDescription),
        [widget.actions],
    );

    const isShowGreyHintButton = !isError && !!helperAction?.widgetId;
    const isShowErrorHintFromValidation = !!isError && !!failedValidationInternalId;
    const isShowErrorHintFromServer = initialIsError && !!errorHelperAction?.widgetId;
    const isShowErrorHintButton = failedValidationInternalId === undefined ? isShowErrorHintFromServer : isShowErrorHintFromValidation;
    const handleHintClick = helperAction ? () => triggerAction(helperAction) : undefined;

    const handleErrorClick = useCallback(() => {
        if (isShowErrorHintFromValidation && failedValidationInternalId) {
            const action: TWAction = {
                widgetId: failedValidationInternalId,
                type: ActionTypeEnum.Player,
                actionType: ActionActionTypeEnum.onClick,
                name: ConsumerActionPlayerNameEnums.showErrorHelperDescription,
                page: errorHelperAction?.page ?? '',
            };
            void triggerAction(action);
        } else if (isShowErrorHintFromServer && errorHelperAction) {
            void triggerAction(errorHelperAction);
        }
    }, [isShowErrorHintFromValidation, isShowErrorHintFromServer, failedValidationInternalId, errorHelperAction, triggerAction]);

    // This function temporarily sets the input field to readonly to prevent the password manager from triggering.
    // When the input field is focused, we remove the readonly attribute.
    const handleFocusWithReadOnly = (e: React.FocusEvent<HTMLInputElement>) => {
        if (handleFocus) {
            handleFocus(e);
        }
    };

    return (
        <CoreInputView
            uid={widget.uid}
            value={value || ''}
            label={label}
            isError={isError}
            helperText={helperText}
            isFocused={isFocused}
            isDisabled={isDisabled}
            isHide={isHidden}
            inputRef={inputRef}
            autoComplete={inputAutoComplete}
            name={name}
            autoFocus={options.onFocus || false}
            readOnly={isDisabled}
            type={getInputType(options)}
            inputMode={getInputMode(options)}
            maxLength={data.submitLength || options.maxLength}
            required={data.mandatory}
            tabIndex={data.tabIndex}
            onChange={(e) => handleChange?.(e)}
            onBlur={handleBlur}
            onFocus={handleFocusWithReadOnly}
            renderWrapper={(input) => (
                <InputWrapper
                    dataTest={widget.uid}
                    widget={widget}
                    useTheme
                    isDisabled={isDisabled}
                    isHidden={isHidden}
                    labelText={label}
                    isFocused={isFocused}
                    isError={isError}
                    errorMessage={errorMessage}
                    helperText={helperText}
                    isFilled={isFilled}
                    isShowGreyHintButton={isShowGreyHintButton}
                    isShowErrorHintButton={isShowErrorHintButton}
                    handleHintClick={handleHintClick}
                    handleErrorClick={handleErrorClick}
                    marginTop={options.margin?.top}
                    marginRight={options.margin?.right}
                    marginBottom={options.margin?.bottom}
                    marginLeft={options.margin?.left}
                >
                    {input}
                </InputWrapper>
            )}
        />
    );
};

export { InputText };
